/**
 * Which hue stands for which company, decided from the company's own name.
 *
 * `shared.tsx`'s `hue = (i) => SERIES_FILL[i % 4]` colours by RANK and wraps
 * by modulo, and both are the chart asserting something the rows do not say.
 * By rank, a holding changes colour when it slips from third-largest to
 * fourth, so the reader who learnt "the blue one is NABIL" on the positions
 * card learns it again on the next one. By modulo, the fifth entity is handed
 * the first entity's hue -- two rows claiming one identity, on a surface whose
 * whole grammar is "which of these is the one I asked about". That is not a
 * palette shortfall, it is the chart stating a fact that is false.
 *
 * So identity is derived from the SYMBOL, with no server round trip and no
 * state: NABIL hashes to the same preference wherever it is drawn, which is
 * what makes one hue readable across the allocation card, the positions card
 * and next turn's comparison. Where two symbols on one card want the same
 * slot the later one steps to its next preference -- a different hue on that
 * card only, and the guarantee is stated honestly rather than overclaimed.
 *
 * PAST THE LAST SLOT IT FOLDS, AND NEVER WRAPS. `"rest"` is not a fifth hue;
 * it means "not individually identified", which is a true thing to say about
 * the fifth of ten sectors. A wrapped hue is a lie about a fact.
 *
 * FOUR SLOTS, AND THE COUNT IS MEASURED RATHER THAN CHOSEN. It was eight for
 * a day, validated on ADJACENT pairs -- the right gate for an ordered ramp,
 * the wrong one for this. A donut's arcs, a stacked bar's segments and a
 * legend column all put arbitrary slots side by side, so the gate is every
 * pair, and under it eight failed in both themes: two slots were the same
 * colour to a deuteranopic reader in dark. `app/embed.css` carries the whole
 * search -- eight, seven, six and five each fail a named gate, and the
 * binding theme is dark, where the Forest card's own 3:1 floor leaves a
 * corridor 0.09 of OKLCH lightness wide. Fewer slots means the tail is
 * reached sooner, which is a truthful chart rather than a poorer one: the
 * fifth holding is drawn as one of the rest, and it is one of the rest.
 *
 * IDENTITY IS FOR MARKS, NEVER FOR TEXT. A bar, a stack segment, a line, a
 * legend swatch -- things that stand for one entity. A coloured ticker reads
 * as a link, and `Card.tsx` already argues an identifier is a mark on
 * `bg-ground` in `text-ink-mid`. `fill()` is for anything 8px or more in its
 * smallest dimension; `ink()` and `stroke()` are the 4.6:1 pairs, for text
 * that is not an identifier and for strokes of 2px or less.
 */

/** A hue slot, or the tail. `"rest"` is a statement, not a colour choice. */
export type Slot = 1 | 2 | 3 | 4 | 'rest'

/** How many hues the ramp carries. Four in both themes, with -ink partners. */
export const SLOTS = 4

/**
 * The ramps, written out in full.
 *
 * Tailwind scans source text, so `bg-series-${n}` is a class that does not
 * exist in the build -- and its failure mode is a colourless chart rather than
 * an error, which is the kind that ships. A test asserts no template literal
 * builds a series class in this file.
 */
const SERIES_FILL = {
  1: 'bg-series-1',
  2: 'bg-series-2',
  3: 'bg-series-3',
  4: 'bg-series-4',
} as const

const SERIES_INK = {
  1: 'text-series-1-ink',
  2: 'text-series-2-ink',
  3: 'text-series-3-ink',
  4: 'text-series-4-ink',
} as const

const SERIES_STROKE = {
  1: 'stroke-series-1-ink',
  2: 'stroke-series-2-ink',
  3: 'stroke-series-3-ink',
  4: 'stroke-series-4-ink',
} as const

/* THE TAIL IS A PAIR TOO, SINCE 2026-09-10, and it is the same rule every
   slot above obeys: a fill is a graphical object at 3:1 and text owes 4.5,
   and on the dark card those two floors pull in opposite directions. Held in
   one value the text floor won, and the mark meaning "not individually
   identified" came out as the brightest thing on the card. */
const REST_FILL = 'bg-series-rest'
const REST_INK = 'text-series-rest-ink'
const REST_STROKE = 'stroke-series-rest-ink'

/** The filled mark for a slot: a bar, a segment, a swatch. */
export function fill(slot: Slot): string {
  return slot === 'rest' ? REST_FILL : SERIES_FILL[slot]
}

/** The same hue as TEXT, which is a different contrast floor -- the `-ink`
 *  half of each pair. Never on an identifier; see the module note. */
export function ink(slot: Slot): string {
  return slot === 'rest' ? REST_INK : SERIES_INK[slot]
}

/** The same hue as a STROKE, for a chart line or a 2px rule. Shares the -ink
 *  half, because a thin mark is judged at the text floor, not the 3:1 one. */
export function stroke(slot: Slot): string {
  return slot === 'rest' ? REST_STROKE : SERIES_STROKE[slot]
}

/**
 * One entity key, in the one spelling everything downstream compares against.
 *
 * An absent key normalises to `""`, which is how "this row names no entity"
 * stays distinguishable from "this row names an entity called nothing".
 */
function normalise(key: string | null | undefined): string {
  return (key ?? '').trim().toUpperCase()
}

/**
 * 32-bit FNV-1a over the normalised key.
 *
 * Chosen for being short, dependency-free and identical in every runtime that
 * runs this file -- the hue has to survive a page reload and a different
 * device, so a seeded or address-derived hash is disqualified. `charCodeAt`
 * reads UTF-16 code units, which is deterministic for Devanagari as well as
 * for a ticker; nothing here inspects digits, so the \p{Nd} rule does not
 * reach it.
 */
function hash32(text: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    // Math.imul, because `h * 0x01000193` leaves the float range and the
    // low bits -- the ones that decide the slot -- come back wrong.
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/**
 * A key's slots in the order it wants them: its own hue first, then the
 * rotation. The rotation rather than a second hash, so that a collision
 * displaces one entity by one step instead of scattering both.
 */
function preferences(normalised: string): Slot[] {
  const first = hash32(normalised) % SLOTS
  const order: Slot[] = []
  for (let k = 0; k < SLOTS; k++) {
    order.push((((first + k) % SLOTS) + 1) as Slot)
  }
  return order
}

/**
 * The hue every entity on ONE drawing gets. Call it once per card, after any
 * sort the shape performs, and read the map everywhere that card names an
 * entity -- two calls on one card can disagree about a collision.
 *
 * Keyed by the normalised key, so read it through `slotOf` rather than
 * `map.get` unless the caller has already normalised. Its iteration order is
 * the resolution order, not the draw order; nothing should read it as a list.
 */
export function assign(keys: readonly (string | null | undefined)[]): Map<string, Slot> {
  // A bare string is iterable, so without this `assign("NABIL")` walks five
  // characters and draws five hues for one company, silently and plausibly.
  // Read through a boolean rather than branching on the predicate directly:
  // `Array.isArray` narrows a readonly array to `any[]`, which would make
  // every `raw` below an `any` for the rest of the function.
  const isList: boolean = Array.isArray(keys)
  if (!isList) {
    throw new TypeError('assign takes a list of entity keys, not one key')
  }

  const slots = new Map<string, Slot>()

  // DRAW ORDER DECIDES WHO IS IDENTIFIED, and only that. An allocation sorts
  // descending before it calls, so the four largest slices are the four the
  // reader can name and the tail is honestly the tail. Reversing that -- a
  // set-determined fold -- would grey out the biggest number on the card.
  const identified: string[] = []
  for (const raw of keys) {
    const key = normalise(raw)
    // Consumes no slot: a row that names no entity has not used up a hue the
    // row after it, which does name one, is entitled to.
    if (key === '' || slots.has(key)) continue
    if (identified.length < SLOTS) {
      identified.push(key)
      continue
    }
    // Past the fourth. `"rest"` rather than a wrapped hue -- see the module
    // note: a repeated hue is two entities claiming one identity.
    slots.set(key, 'rest')
  }

  // THE SET DECIDES WHICH HUE, NOT THE ORDER, and this is a deliberate
  // departure from "walk in draw order". Draw order is a SORT -- by weight,
  // by name, by change -- and resolving collisions along it means a holding
  // that slips from third-largest to fourth can hand its hue to the holding
  // that passed it. That is the rank defect this module exists to end,
  // arriving through the collision path instead of the index one. Resolving
  // in a canonical order derived from the keys themselves makes the map a
  // pure function of the SET, so a re-sort of the same entities moves
  // nothing. Narrowing the ramp made this path the common one rather than the
  // exception: four entities land in four slots without a collision 9.4% of
  // the time (4! / 4^4) against 41.0% for eight, so nearly every card resolves
  // one, and resolving it along the sort would have moved a hue on nearly
  // every card.
  const contest = identified
    .map((key) => ({ key, rank: hash32(key) }))
    .sort((a, b) => a.rank - b.rank || (a.key < b.key ? -1 : 1))

  const taken = new Set<Slot>()
  for (const { key } of contest) {
    const free = preferences(key).find((slot) => !taken.has(slot))
    // Unreachable while `identified` is capped at SLOTS -- four keys cannot
    // exhaust four full rotations -- but the fold is the module's rule, not
    // an arithmetic accident, so it is written where the rule lives.
    if (free === undefined) {
      slots.set(key, 'rest')
      continue
    }
    taken.add(free)
    slots.set(key, free)
  }

  return slots
}

/** A key's slot on the card `assign` was called for. Anything the card did not
 *  identify -- absent, blank, or past the fourth entity -- is `"rest"`. */
export function slotOf(slots: ReadonlyMap<string, Slot>, key: string | null | undefined): Slot {
  return slots.get(normalise(key)) ?? 'rest'
}

/** `assign` and its lookup in one, for the call site that reads
 *  `fill(hueOf(position.symbol))`. Still one allocation per card. */
export function assigner(
  keys: readonly (string | null | undefined)[],
): (key: string | null | undefined) => Slot {
  const slots = assign(keys)
  return (key) => slotOf(slots, key)
}

/**
 * The hue for a CATEGORY that is not an entity -- the parts of one total, a
 * fee against its base, the segments of a charge breakdown.
 *
 * Deliberately a different name and a different argument, because the two
 * ramps cannot be swapped without the chart lying either way round. By index
 * here is CORRECT: the parts of one total have no identity to carry between
 * cards, and their order is the total's own. By index for an entity is the
 * rank defect this module exists to end -- so a caller holding a symbol has
 * nothing here to pass it to, and a caller holding an index has no `assign`
 * overload that takes one.
 *
 * Folds past the fourth part for the same reason `assign` does, and folds a
 * non-index rather than coercing it: a hue chosen off a coerced NaN is a hue
 * chosen at random.
 */
export function categorySlot(index: number): Slot {
  if (!Number.isInteger(index) || index < 0 || index >= SLOTS) return 'rest'
  return (index + 1) as Slot
}
